/**
 * Global Network Health Controller (Singleton)
 *
 * - Pings /api/v1/health/ every 60s when online and the tab is visible
 * - Skips scheduled pings while the tab is hidden (visibilitychange re-pings
 *   on return); offline recovery pings still run in the background
 * - If ping fails → IMMEDIATELY offline, block all requests
 * - When offline → retry every 10-30s
 * - Ping succeeds again → online, emit 'recovered' with duration data
 * - After 5min offline → emit 'stale'
 *
 * Events emitted:
 *   'offline'    — { timestamp }
 *   'recovered'  — { offlineSince, recoveredAt, durationSec }
 *   'stale'      — { offlineSince, durationSec }
 */
import { ref, readonly } from 'vue'
import { isNative } from '@/native/platform'
import { onConnectivityChange } from '@/native/network'

// Native shells run on capacitor://localhost / http://localhost, so a relative
// '/api/v1/health/' would resolve against the WebView origin (which serves the
// static bundle, not the API) and the ping would always "fail" → app stuck
// offline. Build an absolute URL from VITE_API_BASE_URL when present; on web the
// var is unset and we keep the same-origin relative path.
const API_BASE = (import.meta.env?.VITE_API_BASE_URL || '').replace(/\/+$/, '')
const HEALTH_URL = API_BASE ? `${API_BASE}/api/v1/health/` : '/api/v1/health/'
const PING_ONLINE = 60000
const PING_OFFLINE_MIN = 10000
const PING_OFFLINE_MAX = 30000
const REQUEST_TIMEOUT = 5000
const STALE_TIMEOUT = 600000

const isOnline = ref(true)
const status = ref('online')
const offlineSince = ref(null)
const isStale = ref(false)

let timer = null
let staleTimer = null
let started = false
let firstPingDone = null
const subs = new Set()

let consecutivePingFailures = 0
const OFFLINE_THRESHOLD = 4

let reportFailureCount = 0
let reportFailureFirstTime = 0
const REPORT_FAILURE_THRESHOLD = 6
const REPORT_FAILURE_WINDOW = 30000

function nextInterval() {
  if (isOnline.value) return PING_ONLINE
  const elapsed = Date.now() - (offlineSince.value || Date.now())
  const exp = Math.min(Math.floor(elapsed / 15000), 3)
  return Math.min(PING_OFFLINE_MIN * Math.pow(2, exp), PING_OFFLINE_MAX)
}

function schedule() {
  if (timer) clearTimeout(timer)
  timer = setTimeout(ping, nextInterval())
}

function emit(event, data) {
  for (const fn of subs) {
    try { fn(event, data) } catch (e) { /* silent */ }
  }
}

function goOffline() {
  if (!isOnline.value) return
  const ts = Date.now()
  console.log(
    '%c ✖ DISCONNECTED — server unreachable, all requests blocked ',
    'background:#ef4444;color:white;font-size:14px;padding:4px 8px;border-radius:4px;font-weight:bold'
  )
  isOnline.value = false
  status.value = 'offline'
  offlineSince.value = ts
  emit('offline', { timestamp: ts })
  staleTimer = setTimeout(() => {
    isStale.value = true
    const dur = Math.round((Date.now() - ts) / 1000)
    console.log(
      '%c ⚠ OFFLINE > 5 min — recommend page reload ',
      'background:#ff9966;color:white;font-size:14px;padding:4px 8px;border-radius:4px;font-weight:bold'
    )
    emit('stale', { offlineSince: ts, durationSec: dur })
  }, STALE_TIMEOUT)
}

function goOnline() {
  const wasOff = !isOnline.value
  const offTs = offlineSince.value
  isOnline.value = true
  status.value = 'online'
  isStale.value = false
  if (staleTimer) { clearTimeout(staleTimer); staleTimer = null }
  if (rapidRetryTimer) { clearTimeout(rapidRetryTimer); rapidRetryTimer = null }
  if (wasOff) {
    const now = Date.now()
    const sec = offTs ? Math.round((now - offTs) / 1000) : 0
    console.log(
      '%c ✔ RECONNECTED — server is back! Was offline ' + sec + ' seconds. Refreshing all data now... ',
      'background:#22c55e;color:white;font-size:16px;padding:6px 12px;border-radius:6px;font-weight:bold'
    )
    offlineSince.value = null
    emit('recovered', {
      offlineSince: offTs,
      recoveredAt: now,
      durationSec: sec
    })
  }
}

async function ping() {
  // Background tabs must not hammer /health/ — each open SPA tab used to ping
  // every 60s even when hidden (prod: ~1750 health hits / 10 min). When the
  // tab becomes visible again, visibilitychange already triggers a ping.
  // While offline we still probe in the background so recovery is not delayed
  // until the user returns to the tab.
  if (typeof document !== 'undefined'
      && document.visibilityState !== 'visible'
      && isOnline.value) {
    schedule()
    return
  }
  try {
    const ac = new AbortController()
    const t = setTimeout(() => ac.abort(), REQUEST_TIMEOUT)
    const res = await fetch(HEALTH_URL, { method: 'GET', signal: ac.signal, cache: 'no-store' })
    clearTimeout(t)
    if (res.ok) {
      consecutivePingFailures = 0
      goOnline()
    } else {
      consecutivePingFailures++
      if (consecutivePingFailures >= OFFLINE_THRESHOLD) {
        goOffline()
      } else {
        console.log(`[NH] Ping failed (${consecutivePingFailures}/${OFFLINE_THRESHOLD}) — retrying before going offline`)
        setTimeout(ping, 3000)
      }
    }
  } catch {
    consecutivePingFailures++
    if (consecutivePingFailures >= OFFLINE_THRESHOLD) {
      goOffline()
    } else {
      console.log(`[NH] Ping error (${consecutivePingFailures}/${OFFLINE_THRESHOLD}) — retrying before going offline`)
      setTimeout(ping, 3000)
    }
  }
  schedule()
}

let rapidRetryTimer = null

function rapidRecoveryPing(attempt) {
  if (isOnline.value || attempt >= 5) return
  ping()
  rapidRetryTimer = setTimeout(() => rapidRecoveryPing(attempt + 1), 2000)
}

function start() {
  if (started) return firstPingDone
  started = true
  console.log('[NH] started')
  firstPingDone = ping()
  window.addEventListener('online', () => {
    console.log('[NH] browser says: online — rapid-pinging to recover fast...')
    if (rapidRetryTimer) clearTimeout(rapidRetryTimer)
    rapidRecoveryPing(0)
  })
  window.addEventListener('offline', () => { console.log('[NH] browser says: offline'); goOffline() })
  document.addEventListener('visibilitychange', () => { if (!document.hidden) ping() })

  // Native: @capacitor/network is a far more reliable connectivity source than
  // navigator.onLine inside a WebView. A "disconnected" event is authoritative
  // (radio is off) so we go offline immediately; a "connected" event triggers a
  // fast recovery ping (we still confirm reachability via the health endpoint —
  // connected to Wi-Fi != server reachable).
  if (isNative()) {
    onConnectivityChange((connected) => {
      if (connected) {
        console.log('[NH] capacitor/network: connected — rapid-pinging to recover...')
        if (rapidRetryTimer) clearTimeout(rapidRetryTimer)
        rapidRecoveryPing(0)
      } else {
        console.log('[NH] capacitor/network: disconnected')
        goOffline()
      }
    }).catch(() => { /* plugin missing — health ping still covers us */ })
  }
  return firstPingDone
}

export async function initNetworkHealth() { return start() }
export function getIsOnline() { return isOnline.value }

export function reportNetworkFailure() {
  if (!isOnline.value) return
  const now = Date.now()
  if (now - reportFailureFirstTime > REPORT_FAILURE_WINDOW) {
    reportFailureCount = 0
    reportFailureFirstTime = now
  }
  reportFailureCount++
  if (reportFailureCount >= REPORT_FAILURE_THRESHOLD) {
    console.warn(`[NH] ${REPORT_FAILURE_THRESHOLD} network failures in ${REPORT_FAILURE_WINDOW/1000}s — going offline`)
    reportFailureCount = 0
    goOffline()
    ping()
  } else {
    console.log(`[NH] Network failure reported (${reportFailureCount}/${REPORT_FAILURE_THRESHOLD}) — not going offline yet`)
    ping()
  }
}

function onNetworkEvent(cb) {
  subs.add(cb)
  return () => subs.delete(cb)
}

export function useNetworkHealth() {
  start()
  return {
    isOnline: readonly(isOnline),
    status: readonly(status),
    offlineSince: readonly(offlineSince),
    isStale: readonly(isStale),
    onNetworkEvent,
    checkNow: ping
  }
}
